import { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import { COLLECTIONS, COLORS, USER_ROLES, PAYMENT_STATUS, SUBSCRIPTION_STATUS, CONTENT_STATUS } from '../utils/constants';
import { useAuth } from '../contexts/AuthContext';
import { subscribeToSubscriptions, calculateSubscriptionStats } from '../services/subscriptionService';

export const useDashboardData = () => {
  const { userProfile } = useAuth();
  const [data, setData] = useState({
    // Aggregated Stats
    totalStudents: 0,
    totalTeachers: 0,
    totalLessons: 0,
    activeSubscriptions: 0,
    monthlyRevenue: 0,
    pendingApprovals: 0,
    
    // Chart Data
    revenueData: [],
    subscriptionData: [],
    studentGrowthData: [],
    topSubjectsData: [
      { subject: 'Mathematics', students: 8945 },
      { subject: 'English', students: 8234 },
      { subject: 'Physics', students: 6789 },
      { subject: 'Chemistry', students: 6123 },
      { subject: 'Biology', students: 5678 },
    ],
    
    // Tables Data
    recentStudentsData: [],
    recentPaymentsData: [],
    
    loading: true,
  });

  useEffect(() => {
    if (!userProfile) return;

    if (userProfile.role !== 'admin') {
      setData(prev => ({ ...prev, loading: false }));
      return;
    }

    const unsubs = [];
    let loadCount = 0;
    const TOTAL_COLLECTIONS = 5;

    const checkLoading = () => {
      loadCount++;
      if (loadCount >= TOTAL_COLLECTIONS) {
        setData(prev => ({ ...prev, loading: false }));
      }
    };

    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const getLast6Months = () => {
      const months = [];
      const now = new Date();
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        months.push({ 
          label: monthNames[d.getMonth()], 
          month: d.getMonth(), 
          year: d.getFullYear() 
        });
      }
      return months;
    };

    // 1. Users Subscription
    let usersMap = {};
    let latestPayments = [];

    const formatPayments = (payments, uMap) => {
      return payments.slice(0, 5).map(p => {
        const user = (p.userId && uMap[p.userId]) ? uMap[p.userId] : null;
        
        const studentEmail = p.email || p.customerEmail || p.paystackResponse?.customer?.email || user?.email || '';
        let studentName = p.student || p.studentName || p.userName || user?.fullName || '';
        
        // If student name is missing or is just the raw userId, fall back to user's name, email username, or 'Student'
        if (!studentName || studentName === p.userId) {
          studentName = user?.fullName || (studentEmail ? studentEmail.split('@')[0] : 'Student');
        }

        return {
          id: p.id,
          transactionId: p.reference || p.transactionId || (p.id ? p.id.substring(0, 10).toUpperCase() : 'N/A'),
          student: studentName,
          email: studentEmail,
          amount: `₦${parseFloat(p.amount?.toString().replace(/[^0-9.-]+/g, '') || 0).toLocaleString()}`,
          status: (p.status || '').toLowerCase() === 'success' ? 'Success' : ((p.status || '').toLowerCase() === 'failed' ? 'Failed' : 'Pending'),
          date: p.createdAt?.toDate ? p.createdAt.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : (p.date || 'Unknown'),
        };
      });
    };

    const usersQ = query(collection(db, COLLECTIONS.USERS), orderBy('createdAt', 'desc'));
    unsubs.push(onSnapshot(usersQ, (snapshot) => {
      const users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      users.forEach(u => { usersMap[u.id] = u; });
      
      const students = users.filter(u => u.role === USER_ROLES.STUDENT);
      const teachers = users.filter(u => u.role === USER_ROLES.TEACHER);
      
      // Calculate Student Growth (Registrations in last 6 months)
      const last6Months = getLast6Months();
      const studentGrowthData = last6Months.map(m => {
        const count = students.filter(s => {
          if (!s.createdAt) return false;
          const d = s.createdAt.toDate ? s.createdAt.toDate() : new Date(s.createdAt);
          return d.getMonth() === m.month && d.getFullYear() === m.year;
        }).length;
        return { month: m.label, students: count };
      });
      
      // Make it cumulative for better charting
      let runningTotal = students.length - studentGrowthData.reduce((acc, curr) => acc + curr.students, 0);
      studentGrowthData.forEach(item => {
        runningTotal += item.students;
        item.students = runningTotal;
      });

      // Recent students (Top 5)
      const recentStudentsData = students.slice(0, 5).map(s => ({
        id: s.id,
        name: s.fullName || 'Unknown',
        class: s.classLevel || 'N/A',
        email: s.email || 'N/A',
        status: s.isActive ? 'Active' : 'Inactive',
        joinedDate: s.createdAt?.toDate ? s.createdAt.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Unknown',
      }));

      setData(prev => ({
        ...prev,
        totalStudents: students.length,
        totalTeachers: teachers.length,
        studentGrowthData,
        recentStudentsData,
        ...(latestPayments.length > 0 && { recentPaymentsData: formatPayments(latestPayments, usersMap) }),
      }));
      checkLoading();
    }));

    // 2. Payments Subscription
    const paymentsQ = query(collection(db, COLLECTIONS.PAYMENTS), orderBy('createdAt', 'desc'));
    unsubs.push(onSnapshot(paymentsQ, (snapshot) => {
      const payments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      latestPayments = payments;
      
      const thisMonth = new Date();
      thisMonth.setDate(1);
      thisMonth.setHours(0,0,0,0);
      
      let monthlyRevenue = 0;
      payments.forEach(p => {
        if (p.status === PAYMENT_STATUS.SUCCESS) {
          const pDate = p.createdAt?.toDate ? p.createdAt.toDate() : new Date(p.createdAt || 0);
          if (pDate >= thisMonth) {
             monthlyRevenue += parseFloat(p.amount?.toString().replace(/[^0-9.-]+/g, '') || 0);
          }
        }
      });
      
      // Revenue Chart Data (Last 6 Months)
      const last6Months = getLast6Months();
      const revenueData = last6Months.map(m => {
        const rev = payments.filter(p => {
          if (p.status !== PAYMENT_STATUS.SUCCESS || !p.createdAt) return false;
          const d = p.createdAt.toDate ? p.createdAt.toDate() : new Date(p.createdAt);
          return d.getMonth() === m.month && d.getFullYear() === m.year;
        }).reduce((sum, p) => sum + parseFloat(p.amount?.toString().replace(/[^0-9.-]+/g, '') || 0), 0);
        
        return { month: m.label, revenue: rev };
      });

      // Recent Payments
      const recentPaymentsData = formatPayments(payments, usersMap);

      setData(prev => ({
        ...prev,
        monthlyRevenue,
        revenueData,
        recentPaymentsData,
      }));
      checkLoading();
    }));

    // 3. Subscriptions Subscription (via collectionGroup 'subjects')
    let subsLoaded = false;
    const unsubSubs = subscribeToSubscriptions((subs) => {
      const stats = calculateSubscriptionStats(subs);
      const pending = subs.filter(s => s.status === 'pending').length;

      setData(prev => ({
        ...prev,
        activeSubscriptions: stats.active,
        subscriptionData: [
          { name: 'Active', value: stats.active, color: COLORS.success },
          { name: 'Expired', value: stats.expired, color: COLORS.danger },
          { name: 'Pending', value: pending, color: COLORS.warning },
        ]
      }));

      if (!subsLoaded) {
        subsLoaded = true;
        checkLoading();
      }
    });
    unsubs.push(unsubSubs);

    // 4. Syllabus Subscription (for Total Lessons in syllabus, if any)
    let syllabusLessons = 0;
    let contentLessons = 0;

    const syllabusQ = query(collection(db, COLLECTIONS.SYLLABUS), where('type', '==', 'lesson'));
    unsubs.push(onSnapshot(syllabusQ, (snapshot) => {
      syllabusLessons = snapshot.size;
      setData(prev => ({
        ...prev,
        totalLessons: contentLessons + syllabusLessons,
      }));
      checkLoading();
    }, (err) => {
      console.warn('Syllabus lessons listener error:', err);
      checkLoading();
    }));

    // 5. Content Approval Subscription (for Lessons & Pending Approvals)
    const contentAppQ = query(collection(db, COLLECTIONS.CONTENT_APPROVAL));
    unsubs.push(onSnapshot(contentAppQ, (snapshot) => {
      const allContent = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const pendingCount = allContent.filter(c => 
        (c.status || '').toLowerCase() === CONTENT_STATUS.PENDING
      ).length;

      const approvedLessons = allContent.filter(c => 
        c.type?.toString().toLowerCase() === 'lesson' && (c.status || '').toLowerCase() === 'approved'
      ).length;

      const allValidLessons = allContent.filter(c => 
        c.type?.toString().toLowerCase() === 'lesson' && (c.status || '').toLowerCase() !== 'rejected'
      ).length;

      contentLessons = approvedLessons > 0 ? approvedLessons : allValidLessons;

      setData(prev => ({
        ...prev,
        pendingApprovals: pendingCount,
        totalLessons: contentLessons + syllabusLessons,
      }));
      checkLoading();
    }, (err) => {
      console.warn('Content approvals listener error:', err);
      checkLoading();
    }));

    return () => {
      unsubs.forEach(unsub => unsub());
    };
  }, [userProfile]);

  return data;
};
